import { isEnumValue } from "@redotech/util/enum";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { zCurrency } from "../currency";

export enum OutboundLabelsTransactionType {
  VOID_LABEL = "VOID_LABEL",
  VOID_USED_LABEL = "VOID_USED_LABEL",
  CHARGE_LABEL = "CHARGE_LABEL",
  ADD_FUNDS = "ADD_FUNDS",
  REFUND = "REFUND",
  MISCELLANEOUS = "MISCELLANEOUS",
  SURCHARGE = "SURCHARGE",
  RETURN_LABEL = "RETURN_LABEL",
  MERCHANT_CARRIER_FEE = "MERCHANT_CARRIER_FEE",
  SUBSCRIPTION_FEE = "SUBSCRIPTION_FEE",
  REDEEM_POINTS = "REDEEM_POINTS",
  INSURANCE = "INSURANCE",
  VOID_INSURANCE = "VOID_INSURANCE",
  TRANSACTION_FAILURE_CLAWBACK = "TRANSACTION_FAILURE_CLAWBACK",
  WMS_FEE = "WMS_FEE",
  REDO_SUPPLY = "REDO_SUPPLY",
  LANDED_COST_DUTIES = "LANDED_COST_DUTIES",
  LANDED_COST_TAXES = "LANDED_COST_TAXES",
  LANDED_COST_FEES = "LANDED_COST_FEES",
}

export const transactionTypeLabels: Record<
  OutboundLabelsTransactionType,
  string
> = {
  [OutboundLabelsTransactionType.VOID_LABEL]: "Void Label",
  [OutboundLabelsTransactionType.VOID_USED_LABEL]: "Used Voided Label",
  [OutboundLabelsTransactionType.CHARGE_LABEL]: "Charge Label",
  [OutboundLabelsTransactionType.ADD_FUNDS]: "Add Funds",
  [OutboundLabelsTransactionType.REFUND]: "Refund",
  [OutboundLabelsTransactionType.MISCELLANEOUS]: "Miscellaneous",
  [OutboundLabelsTransactionType.SURCHARGE]: "Surcharge",
  [OutboundLabelsTransactionType.RETURN_LABEL]: "Return Label",
  [OutboundLabelsTransactionType.MERCHANT_CARRIER_FEE]: "Merchant Carrier Fee",
  [OutboundLabelsTransactionType.SUBSCRIPTION_FEE]: "Subscription Fee",
  [OutboundLabelsTransactionType.REDEEM_POINTS]: "Redeem Points",
  [OutboundLabelsTransactionType.INSURANCE]: "Insurance",
  [OutboundLabelsTransactionType.VOID_INSURANCE]: "Void Insurance",
  [OutboundLabelsTransactionType.TRANSACTION_FAILURE_CLAWBACK]:
    "Transaction Failure Clawback",
  [OutboundLabelsTransactionType.WMS_FEE]: "WMS Fee",
  [OutboundLabelsTransactionType.REDO_SUPPLY]: "Redo Supply",
  [OutboundLabelsTransactionType.LANDED_COST_DUTIES]: "Landed Cost Duties",
  [OutboundLabelsTransactionType.LANDED_COST_TAXES]: "Landed Cost Taxes",
  [OutboundLabelsTransactionType.LANDED_COST_FEES]: "Landed Cost Fees",
};

export function getTransactionTypeLabel(
  transactionType: OutboundLabelsTransactionType | string,
): string {
  if (isEnumValue(transactionType, OutboundLabelsTransactionType)) {
    return transactionTypeLabels[
      transactionType as OutboundLabelsTransactionType
    ];
  }
  return transactionType;
}

export const BaseBalanceTransactionSchema = z.object({
  amount: z.string(),
  fee: z.string(), // something like a credit card fee that does not apply to their available balance
  currency: zCurrency,
  description: z.string(),
  paymentIntentId: z.string().nullable(),
  paymentDetails: z
    .object({ institution: z.string().nullish(), last4: z.string().nullish() })
    .nullish(),
  status: z.string(),
  createdAt: z.date(),
  balanceSnapshot: z.string().nullish(),
});

// Map settings types to a Mongoose model and a Typescript interface
export const balanceTransactionSchemas = {
  "outbound-labels": BaseBalanceTransactionSchema.extend({
    orderId: z.string().nullish(),
    shipmentId: z.string().nullish(),
    batchId: z.string().nullish(),
    transactionType: createEnum(OutboundLabelsTransactionType, {
      enumName: "OutboundLabelsTransactionType",
    }).nullish(),
    justification: z.string().nullish(),
    // Since all carriers have a different way of presenting surcharges, we need key to prevent duplicate charges.
    uniqueIdentifier: z.string().nullish(),
    chargeDate: z.date().nullish(),
    pointsTransactionId: z.string().nullish(),
    pointsEarned: z.number().nullish(),
    fulfillmentGroupId: z.string().nullish(),
    orderNames: z.array(z.string()).nullish(),
    batchName: z.string().nullish(),
    recognitionDate: z.date().nullish(),
  }),
} as const;

export type balanceTransactionTypes = {
  [key in keyof typeof balanceTransactionSchemas]: z.infer<
    (typeof balanceTransactionSchemas)[key]
  >;
};

// This is necessary for backwards compatibility with old transactions
export function getOldTransactionType(
  description: string,
): OutboundLabelsTransactionType {
  if (
    description.includes("Auto-reload") ||
    description.includes("Outbound credit") ||
    description.includes("Balance credit outbound labels")
  ) {
    return OutboundLabelsTransactionType.ADD_FUNDS;
  }
  if (description.includes("Charging label")) {
    return OutboundLabelsTransactionType.CHARGE_LABEL;
  }
  if (description.includes("Refunding label")) {
    return OutboundLabelsTransactionType.VOID_LABEL;
  }
  if (description.includes("Refund")) {
    return OutboundLabelsTransactionType.REFUND;
  }
  return OutboundLabelsTransactionType.MISCELLANEOUS;
}
